import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { whatsappService, WhatsAppNumber, WhatsAppMessage } from "@/services/whatsappService";
import { Lead } from "@/models/Lead";
import { Send, Clock, CheckCircle, XCircle, Loader2, MessageSquare } from "lucide-react";
import { formatDate } from "@/lib/dateFormatter";

interface WhatsAppMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead;
  organizationId: string;
  campaignId?: string;
}

export function WhatsAppMessageDialog({
  isOpen,
  onClose,
  lead,
  organizationId,
  campaignId,
}: WhatsAppMessageDialogProps) {
  const [message, setMessage] = useState("");
  const [selectedWhatsAppNumber, setSelectedWhatsAppNumber] = useState<string>("");
  const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const refreshIntervalRef = useRef<number>();
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      // When dialog opens, load WhatsApp numbers
      loadWhatsAppNumbers();
    }
  }, [isOpen, organizationId]);

  useEffect(() => {
    if (selectedWhatsAppNumber && isOpen) {
      console.log('Selected WhatsApp number changed or dialog opened - loading messages from API');
      // Initial load - ALWAYS get from API for fresh data
      loadMessages(true, true);
      
      // Set up auto-refresh of messages
      if (isAutoRefresh) {
        console.log('Setting up auto-refresh interval for messages');
        refreshIntervalRef.current = window.setInterval(() => {
          // Silent refresh - only from database during auto-refresh to avoid API rate limits
          loadMessages(false, false); 
        }, 5000); // Refresh every 5 seconds
      }
    }
    
    return () => {
      // Clear interval when component unmounts or dialog closes
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [selectedWhatsAppNumber, isOpen, isAutoRefresh]);
  
  // Scroll to bottom of messages when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadWhatsAppNumbers = async () => {
    try {
      setIsLoading(true);
      const numbers = await whatsappService.getWhatsAppNumbers(organizationId);
      const activeNumbers = numbers.filter(n => n.status === 'active');
      setWhatsappNumbers(activeNumbers);
      
      // Auto-select the first active number
      if (activeNumbers.length > 0) {
        setSelectedWhatsAppNumber(activeNumbers[0].id);
      }
    } catch (error) {
      console.error("Error loading WhatsApp numbers:", error);
      toast({
        title: "Error",
        description: "Failed to load WhatsApp numbers",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (showLoading = true, fetchFromApi = true) => {
    console.log(`Loading messages - showLoading: ${showLoading}, fetchFromApi: ${fetchFromApi}`);
    if (showLoading) {
      setIsLoading(true);
    }
    
    try {
      // Get messages from database and optionally from WhatsApp API
      const msgs = await whatsappService.getMessages(lead.id, selectedWhatsAppNumber, fetchFromApi);
      setMessages(msgs);
      
      // If we fetched from API, show success toast
      if (fetchFromApi && !showLoading) {
        toast({
          title: "Messages Synced",
          description: "Messages have been synced with WhatsApp",
          duration: 2000,
        });
      }
    }
    catch (error) {
      console.error("Error loading messages:", error);
      if (showLoading) {
        toast({
          title: "Error",
          description: "Failed to load messages",
          variant: "destructive",
        });
      }
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedWhatsAppNumber) return;

    try {
      setIsSending(true);
      const newMessage = await whatsappService.sendMessage({
        whatsapp_number_id: selectedWhatsAppNumber,
        lead_id: lead.id,
        content: message.trim(),
      });

      // Add the new message to the list
      setMessages([...messages, newMessage]);
      setMessage("");
      
      toast({
        title: "Success",
        description: "Message sent successfully",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const getMessageStatusIcon = (status: string, errorMessage?: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-muted-foreground" aria-label="Sent" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-foreground" aria-label="Delivered" />;
      case 'read':
        return <CheckCircle className="h-4 w-4 text-good" aria-label="Read" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-danger" aria-label={errorMessage || 'Failed to send'} />;
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-mid" aria-label={errorMessage || 'Pending delivery'} />;
      default:
        return <Clock className="h-4 w-4 text-ink-faint" aria-label="Waiting for status update" />;
    }
  };
  
  const handleRetryMessage = async (messageId: string) => {
    try {
      setIsSending(true);
      // First, let's get the message content
      const messageToRetry = messages.find(m => m.id === messageId);
      
      if (!messageToRetry) {
        throw new Error('Message not found');
      }
      
      // Send a new message with the same content
      const newMessage = await whatsappService.sendMessage({
        whatsapp_number_id: selectedWhatsAppNumber,
        lead_id: lead.id,
        content: messageToRetry.content || '',
      });
      
      // Add the new message to the list and refresh
      await loadMessages();
      
      toast({
        title: "Retry Initiated",
        description: "Message retry has been initiated",
      });
    } catch (error) {
      console.error("Error retrying message:", error);
      toast({
        title: "Error",
        description: "Failed to retry message",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl h-[600px]">
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (whatsappNumbers.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>WhatsApp Message</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">
              No active WhatsApp numbers available. Please configure a WhatsApp number in organization settings first.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] h-[600px] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>WhatsApp Message to {lead.first_name} {lead.last_name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-3 overflow-hidden">
          {/* WhatsApp Number Selection */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">From:</span>
            <Select value={selectedWhatsAppNumber} onValueChange={setSelectedWhatsAppNumber}>
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder="Select WhatsApp number" />
              </SelectTrigger>
              <SelectContent>
                {whatsappNumbers.map((number) => (
                  <SelectItem key={number.id} value={number.id}>
                    {number.phone_number} {number.display_name ? `(${number.display_name})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">To: {lead.phone_number}</span>
          </div>

          {/* Messages Area */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {isAutoRefresh ? (
                  <span className="flex items-center">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    Auto-refreshing messages
                  </span>
                ) : (
                  <span>Auto-refresh paused</span>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsAutoRefresh(!isAutoRefresh)}
                className="h-6 text-xs"
              >
                {isAutoRefresh ? "Pause" : "Resume"} auto-refresh
              </Button>
            </div>
            
            <ScrollArea className="flex-1 border rounded-lg p-4 h-[350px] overflow-y-auto">
              <div className="space-y-4">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full py-8">
                    <Loader2 className="h-8 w-8 animate-spin mr-2" />
                    <span>Loading messages...</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <p>No messages yet. Start a conversation!</p>
                    <p className="text-xs mt-2">Messages will appear here once you send one.</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.direction === 'outbound'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-foreground'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            msg.direction === 'outbound' ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            <span>{formatDate(msg.created_at)}</span>
                            {msg.direction === 'outbound' && (
                              <div className="flex items-center gap-2">
                                {getMessageStatusIcon(msg.status, msg.error_message)}
                                {msg.status === 'failed' && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-5 px-1 text-xs text-white hover:text-white hover:bg-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleRetryMessage(msg.id);
                                    }}
                                    disabled={isSending}
                                  >
                                    Retry
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          {msg.error_message && msg.status === 'failed' && (
                            <div className="text-xs text-danger mt-1">
                              Error: {msg.error_message}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Message Input */}
          <div className="flex gap-2 mt-2">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1"
              rows={2}
              maxRows={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim() || isSending}
              className="self-end h-10 px-3"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0 pt-2">
          <div className="text-sm text-muted-foreground">
            {messages.length > 0 && (
              <span>{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end">
            <Button variant="outline" size="sm" onClick={() => loadMessages(true, false)} disabled={isSending || isLoading}>
              Refresh DB
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => loadMessages(true, true)} 
              disabled={isSending || isLoading}
              className="text-good hover:text-good hover:bg-good-bg"
            >
              <div className="flex items-center">
                <MessageSquare className="h-3.5 w-3.5 mr-1" />
                Sync from WhatsApp
              </div>
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}